import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoIcon from '@mui/icons-material/Info';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useAuth } from '../../../contexts/AuthContext';
import { deletePersonnelTransaction } from '../../../services/migration/personnelTransactionService';

// PersonnelActions.tsx'teki Transaction ile uyumlu (gerekli alanlar)
export interface DeletableTransaction {
  id: string;
  amount: number;
  createdAt: string;
  currency: string;
  description: string;
  isAccrual: boolean;
  paymentMethod: string;
  performedBy: string;
  transactionType: 'DEBT' | 'RECEIVABLE' | 'PAYMENT' | 'COLLECTION';
  cashboxName?: string;
  bankName?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  transaction: DeletableTransaction | null;
  onDeleted?: () => void;
}

const PersonnelTransactionDeleteConfirmModal: React.FC<Props> = ({ open, onClose, transaction, onDeleted }) => {
  const { userDetails } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getUserFullName = (): string => {
    if (userDetails?.firstName && userDetails?.lastName) return `${userDetails.firstName} ${userDetails.lastName}`;
    if (userDetails?.firstName) return userDetails.firstName;
    if (userDetails?.email) return userDetails.email;
    return 'Sistem';
  };

  const formatCurrency = (amount: number, currency: string = 'TRY') =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });

  const getTypeText = (type: string) => {
    switch (type) {
      case 'DEBT': return 'Şirket Alacağı (Personel Borcu)';
      case 'RECEIVABLE': return 'Şirket Borcu (Personel Alacağı)';
      case 'PAYMENT': return 'Ödeme';
      case 'COLLECTION': return 'Tahsilat';
      default: return type;
    }
  };

  // Bağlı para hareketi: nakit ise kasa, banka ise banka hesabı. Tahakkukta yok.
  const hasCash = !transaction?.isAccrual && transaction?.paymentMethod === 'cash' && !!transaction?.cashboxName;
  const hasBank = !transaction?.isAccrual && transaction?.paymentMethod === 'bank' && !!transaction?.bankName;
  const hasMovement = hasCash || hasBank;

  const handleDelete = async () => {
    if (!transaction) return;
    setDeleting(true);
    setError(null);
    try {
      await deletePersonnelTransaction(parseInt(transaction.id, 10), getUserFullName());
      if (onDeleted) onDeleted();
      onClose();
    } catch (err: any) {
      const apiError = err?.response?.data?.message || err?.response?.data?.error || err?.message;
      setError(apiError || 'Hareket silinirken bir hata oluştu.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
        <WarningIcon fontSize="large" />
        <Box>
          <Typography variant="h6">Hareket Silme Onayı</Typography>
          {transaction && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {getTypeText(transaction.transactionType)} - {formatCurrency(transaction.amount, transaction.currency)}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {transaction && (
          <>
            <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">İşlem Türü:</Typography>
                  <Typography variant="body1" fontWeight="bold">{getTypeText(transaction.transactionType)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Tutar:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatCurrency(transaction.amount, transaction.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Tarih:</Typography>
                  <Typography variant="body1" fontWeight="bold">{formatDate(transaction.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">İşlemi Yapan:</Typography>
                  <Typography variant="body1" fontWeight="bold">{transaction.performedBy || '-'}</Typography>
                </Grid>
                {transaction.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Açıklama:</Typography>
                    <Typography variant="body2">{transaction.description}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {hasMovement ? (
              <>
                <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>DİKKAT:</strong> Bu hareketle birlikte oluşturulan aşağıdaki{' '}
                    <strong>kasa/banka hareketi de silinecek</strong> ve ilgili bakiye geri alınacaktır. Bu işlem geri alınamaz.
                  </Typography>
                </Alert>

                <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {hasCash ? <AccountBalanceWalletIcon color="success" /> : <AccountBalanceIcon color="info" />}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasCash ? 'Kasa Hareketi' : 'Banka Hareketi'}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {hasCash ? transaction.cashboxName : transaction.bankName}
                    </Typography>
                  </Box>
                  <Chip
                    label={formatCurrency(transaction.amount, transaction.currency)}
                    color={hasCash ? 'success' : 'info'}
                    size="small"
                  />
                </Paper>
              </>
            ) : (
              <Alert severity="info">
                {transaction.isAccrual
                  ? 'Bu bir tahakkuk (maaş/prim hakedişi) kaydıdır; kasa/banka para hareketi bulunmuyor. Yalnızca personel defteri kaydı silinecektir.'
                  : 'Bu harekete bağlı kasa/banka para hareketi bulunmuyor. Yalnızca personel defteri kaydı silinecektir.'}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={deleting}>İptal</Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
          disabled={deleting || !transaction}
        >
          {deleting ? 'Siliniyor...' : (hasMovement ? 'Hareketi ve Kasa/Banka Kaydını Sil' : 'Hareketi Sil')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonnelTransactionDeleteConfirmModal;
